import { memo, useCallback, useMemo, useRef } from 'react'
import { fmtAgo, fmtTemp, fmtTime, fmtWeekday, localDate } from '../../../shared/units'
import { useReadClock } from '../../shell/useReadClock'
import { openMetric, openPlaces } from '../../shell/actions'
import { useFrameBinding, writeText } from '../../shell/bindings'
import { WeatherGlyph } from '../../shell/WeatherGlyph'
import { wxIconId } from '../../icons/codes'
import { WX_MARKUP } from '../../icons/catalog'
import type { ZoneProps } from '../../data/types'
import { dailyTemperatures } from '../../data/temperatures'
import './hero.css'

const prepared = new WeakMap<ZoneProps['data']['frames'], Map<number, { high: string; low: string; label: string }>>()
export function prepareHero(data: ZoneProps['data']) {
  const hit = prepared.get(data.frames)
  if (hit) return hit
  const result = new Map<number, { high: string; low: string; label: string }>()
  if (!data.report) return result
  const report = data.report, timezone = report.location.timezone
  const days = new Map([...dailyTemperatures(report)].map(([date, day]) => [date, {
    high: fmtTemp(day.high, data.units.temp), low: fmtTemp(day.low, data.units.temp),
    label: `${fmtWeekday(date, timezone, new Date(report.current.time))} · calendar day`,
  }]))
  for (const frame of [data.now, ...data.frames]) {
    if (!frame) continue
    const day = days.get(localDate(new Date(frame.time).toISOString(), timezone))
    if (day) result.set(frame.time, day)
  }
  prepared.set(data.frames, result)
  return result
}

function heroBinding(node: HTMLElement, days: ReadonlyMap<number, { high: string; low: string; label: string }>, now: ZoneProps['data']['now'], currentProvenance: string) {
  const temp = node.querySelector('[data-reading]'), condition = node.querySelector('[data-condition]'), feels = node.querySelector('[data-feels]'), stamp = node.querySelector('[data-stamp]'), glyph = node.querySelector<SVGElement>('[data-wx-icon]')
  const provenance = node.querySelector('[data-provenance]')
  const high = node.querySelector('[data-day-high]'), low = node.querySelector('[data-day-low]'), dayLabel = node.querySelector('[data-day-label]')
  const reading = node.querySelector('[data-qa="open-detail"]')
  const saved = node.dataset.offline === 'true' && node.dataset.demo !== 'true'
  return (frame: NonNullable<ZoneProps['data']['now']>) => {
    writeText(temp, frame.temperature); writeText(condition, frame.description); writeText(feels, `Feels like ${frame.feels}`); writeText(stamp, frame.stamp)
    writeText(provenance, `${saved ? 'Offline · ' : ''}${frame === now ? currentProvenance : frame.provenance}`)
    const day = days.get(frame.time)
    writeText(high, day?.high ?? '—'); writeText(low, day?.low ?? '—'); writeText(dayLabel, day?.label ?? 'local day unavailable')
    reading?.setAttribute('aria-description', `${frame.stamp}: ${frame.temperature}, ${frame.description}.`)
    const key = wxIconId(frame.condition, frame.night).replace('wx-v5-', '')
    if (glyph && glyph.dataset.wxIcon !== key) {
      // Trusted generated local contours; swap only a bounded glyph, never the zone or its SVG box.
      glyph.innerHTML = WX_MARKUP[key]
      glyph.dataset.wxIcon = key
    }
  }
}

export const Hero = memo(function Hero({ data }: ZoneProps) {
  const ref = useRef<HTMLElement>(null)
  const readClock = useReadClock(!data.mock && !!data.report)
  const provenance = useMemo(() => {
    if (!data.now || !data.report || data.mock) return data.now?.provenance ?? ''
    const current = data.report.current, station = current.provenance.station
    const stale = readClock - Date.parse(current.time) > 90 * 60_000 || current.provenance.method === 'observation' && station && readClock - Date.parse(station.observed_at) > 90 * 60_000 || data.snapshot?.expiresAt != null && Date.parse(data.snapshot.expiresAt) <= readClock
    const observation = station && Number.isFinite(Date.parse(station.observed_at)) ? ` · ${station.id} obs ${fmtTime(station.observed_at, data.report.location.timezone, data.units.clock)} (${fmtAgo(station.observed_at, readClock)})` : ''
    return `${stale ? 'Stale reading · ' : ''}${data.now.provenance} (${fmtAgo(current.time, readClock)})${observation}`
  }, [data.now, data.report, data.mock, data.snapshot, data.units.clock, readClock])
  const days = useMemo(() => prepareHero(data), [data.report, data.now, data.frames, data.units.temp])
  const bind = useCallback((node: HTMLElement) => {
    node.dataset.state = data.report ? 'ready' : 'loading'
    node.dataset.offline = String(data.offline); node.dataset.demo = String(data.mock)
    const title = node.querySelector<HTMLElement>('.v5-place-title')!
    title.title = data.name; title.setAttribute('aria-label', `Choose a place. ${data.name}`)
    title.toggleAttribute('data-long', data.name.length > 24)
    writeText(node.querySelector('.v5-place-name'), data.name)
    writeText(node.querySelector('.v5-place-region'), data.place.region || data.report?.location.region || '—')
    if (!data.now) {
      for (const key of ['reading', 'day-high', 'day-low']) writeText(node.querySelector(`[data-${key}]`), '—')
      writeText(node.querySelector('[data-condition]'), data.error ? 'Forecast unavailable' : 'Loading forecast')
      writeText(node.querySelector('[data-feels]'), 'Feels like —')
      writeText(node.querySelector('[data-stamp]'), 'Now')
      writeText(node.querySelector('[data-day-label]'), 'local day')
      const glyph = node.querySelector<SVGElement>('[data-wx-icon]')
      if (glyph) { glyph.innerHTML = WX_MARKUP['overcast-day']; glyph.dataset.wxIcon = 'overcast-day' }
      writeText(node.querySelector('[data-provenance]'), data.offline ? 'Offline · no forecast cached for this place' : data.error ? 'Unable to load weather. Refresh in Settings.' : 'Connecting to the weather sources')
      node.querySelector('[data-qa="open-detail"]')?.removeAttribute('aria-description')
    }
    return heroBinding(node, days, data.now, provenance)
  }, [data, days, provenance])
  useFrameBinding(ref, data, bind)
  const today = data.now ? days.get(data.now.time) : null
  // The structure and event handlers are permanent; report/cursor bindings own its readings.
  return useMemo(() => <section ref={ref} className="v5-hero" data-zone="hero" data-state={data.report ? 'ready' : 'loading'} data-offline={data.offline} data-demo={data.mock} aria-label="Current weather">
    <button className="v5-place-title" data-long={data.name.length > 24 || undefined} title={data.name} onClick={openPlaces} aria-label={`Choose a place. ${data.name}`}><span className="v5-place-name">{data.name}</span><span aria-hidden="true">⌄</span></button>
    <p className="v5-local"><span className="v5-place-region">{data.place.region || data.report?.location.region || '—'}</span><span>·</span><span data-stamp>Now</span></p>
    <button className="v5-current" data-qa="open-detail" onClick={() => openMetric('conditions')} aria-label="Open current conditions">
      <span className="v5-temperature" data-reading>{data.now?.temperature ?? '—'}</span>
      <span className="v5-condition"><WeatherGlyph condition={data.now?.condition ?? 'cloudy'} night={data.now?.night} size={48} /><strong data-condition>{data.now?.description ?? (data.error ? 'Forecast unavailable' : 'Loading forecast')}</strong><span data-feels>{data.now ? `Feels like ${data.now.feels}` : 'Feels like —'}</span></span>
    </button>
    <p className="v5-extremes"><span>High <b data-day-high>{today?.high ?? '—'}</b></span><span>Low <b data-day-low>{today?.low ?? '—'}</b></span><small data-day-label>{today?.label ?? 'local day'}</small></p>
    <p className="v5-provenance" data-provenance>{data.now ? `${data.offline && !data.mock ? 'Offline · ' : ''}${provenance}` : data.offline ? 'Offline · no forecast cached for this place' : data.error ? 'Unable to load weather. Refresh in Settings.' : 'Connecting to the weather sources'}</p>
  </section>, [])
})
